import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { nfpApi } from '../services/nfpApi';
import { apiFetch, setAuthToken, setOrgContext } from '../api/client';
import tealBack from '../Images/teal_dback.png';
import rypilLo from '../Images/rypilLo.png';

const PEACH = '#EBA477';

const STATUS_TONES = {
  not_started: '#F87171',
  in_progress: '#FBBF24',
  submitted_for_review: '#FBBF24',
  needs_attention: '#F87171',
  verified: '#34D399',
};

const DOCUMENT_STATE_STYLES = {
  missing: { label: 'Missing', color: '#F87171', background: 'rgba(248,113,113,0.16)' },
  draft_uploaded: { label: 'Draft uploaded', color: '#FBBF24', background: 'rgba(251,191,36,0.16)' },
  submitted_for_review: { label: 'Submitted for review', color: '#FBBF24', background: 'rgba(251,191,36,0.16)' },
  needs_attention: { label: 'Needs attention', color: '#F87171', background: 'rgba(248,113,113,0.16)' },
  verified: { label: 'Verified', color: '#34D399', background: 'rgba(52,211,153,0.16)' },
};

function StatusChip({ label, tone }) {
  return (
    <span style={{ ...styles.statusChip, borderColor: tone, color: tone }}>
      <span style={{ ...styles.statusDot, background: tone }} />
      {label}
    </span>
  );
}

function SummaryRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={styles.summaryRow}>
      <div style={styles.summaryLabel}>{label}</div>
      <div style={styles.summaryValue}>{value}</div>
    </div>
  );
}

function getDocumentState(doc, proofStatus) {
  if (!doc?.uploaded) return DOCUMENT_STATE_STYLES.missing;
  if (proofStatus === 'verified') return DOCUMENT_STATE_STYLES.verified;
  if (proofStatus === 'rejected') return DOCUMENT_STATE_STYLES.needs_attention;
  if (proofStatus === 'pending_review') return DOCUMENT_STATE_STYLES.submitted_for_review;
  return DOCUMENT_STATE_STYLES.draft_uploaded;
}

function VerificationUploadRow({
  doc,
  proofStatus,
  canRemoveDraftUploads,
  busy,
  onChoose,
  onRemove,
}) {
  const state = getDocumentState(doc, proofStatus);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    await onChoose(file);
  };

  return (
    <div style={styles.uploadRow}>
      <div style={{ flex: '1 1 auto' }}>
        <div style={styles.uploadHeader}>
          <div style={styles.uploadLabel}>{doc.label}</div>
          <span
            style={{
              ...styles.docStateChip,
              color: state.color,
              borderColor: state.color,
              background: state.background,
            }}
          >
            {state.label}
          </span>
        </div>
        <div style={styles.uploadHelper}>{doc.helperText}</div>
        <div style={styles.statusLine}>
          <span style={styles.statusLineLabel}>Status</span>
          <span style={{ color: state.color, fontWeight: 700 }}>{state.label}</span>
        </div>
        <div style={styles.uploadState}>
          {doc.uploaded ? 'Current file attached' : 'No file attached'}
          {doc.url ? (
            <a
              href={doc.url}
              target="_blank"
              rel="noreferrer"
              style={styles.fileLink}
            >
              View current file
            </a>
          ) : null}
        </div>
        <div style={styles.uploadFallback}>{doc.fallbackText}</div>
      </div>
      <div style={styles.uploadActions}>
        <label style={styles.secondaryBtn}>
          {doc.uploaded ? 'Replace' : 'Upload'}
          <input
            type="file"
            accept="application/pdf,image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
            disabled={busy}
          />
        </label>
        {doc.uploaded && canRemoveDraftUploads ? (
          <button type="button" style={styles.removeBtn} onClick={onRemove} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function NfpVerification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [verification, setVerification] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        setLoading(true);
        const resp = await nfpApi.getVerification();
        if (!cancelled) setVerification(resp);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load verification details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const requiredDocuments = verification?.requiredDocuments || [];
  const uploadedDocumentCount = requiredDocuments.filter((doc) => doc.uploaded).length;
  const submitDisabled = !verification?.canSubmit || submitBusy;
  const submitLabel = submitBusy
    ? 'Submitting…'
    : verification?.proofStatus === 'pending_review'
    ? 'Submitted for review'
    : verification?.proofStatus === 'verified'
    ? 'Verified'
    : 'Submit for review';

  const statusTone = STATUS_TONES[verification?.displayStatus?.code] || '#94A3B8';
  const reviewMessage = useMemo(() => {
    const status = verification?.displayStatus?.code;
    const review = verification?.reviewStatus || {};
    if (status === 'verified') {
      return 'Your verification documents have been approved.';
    }
    if (status === 'submitted_for_review') {
      return 'Your documents have been submitted for review. We’ll let you know if anything needs attention.';
    }
    if (status === 'needs_attention') {
      return review.reasonNote || review.rejectionReason || 'Your documents need attention before verification can be completed.';
    }
    if (status === 'in_progress') {
      return 'Draft uploads are saved. Submit them for review when you are ready.';
    }
    return 'Upload the required documents when you are ready to start verification.';
  }, [verification]);

  const refreshVerification = async (promiseFactory, successMessage) => {
    setError('');
    setNotice('');
    try {
      const resp = await promiseFactory();
      setVerification(resp);
      if (successMessage) setNotice(successMessage);
    } catch (e) {
      setError(e?.message || 'Verification update failed.');
    }
  };

  const handleUpload = async (key, file) => {
    setBusyKey(key);
    await refreshVerification(async () => {
      const uploaded = await nfpApi.uploadAsset(file, { kind: 'proof', folder: 'nfp-proof' });
      return nfpApi.saveVerificationProof({ [key]: uploaded });
    }, 'Verification document saved.');
    setBusyKey('');
  };

  const handleRemove = async (key) => {
    setBusyKey(key);
    await refreshVerification(() => nfpApi.saveVerificationProof({ [key]: null }), 'Draft document removed.');
    setBusyKey('');
  };

  const handleSubmit = async () => {
    setSubmitBusy(true);
    await refreshVerification(() => nfpApi.submitVerification(), 'Verification documents submitted for review.');
    setSubmitBusy(false);
  };

  const handleLogout = async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {}
    try {
      setAuthToken(null);
      localStorage.removeItem('user');
      localStorage.removeItem('activeAccountId');
    } catch {}
    setOrgContext(null);
    navigate('/login');
  };

  return (
    <div style={styles.fullscreen}>
      <div style={styles.dashboardMain}>
        <div style={styles.dashboardInner}>
          <div style={styles.headerRow}>
            <div>
              <h1 style={styles.headerTitle}>{verification?.organizationName || 'Verification & Documents'}</h1>
              <div style={styles.headerSub}>Verification &amp; Documents</div>
            </div>
            <div style={styles.headerRight}>
              <StatusChip
                label={verification?.displayStatus?.label || 'Not started'}
                tone={statusTone}
              />
              <button type="button" style={styles.secondaryBtn} onClick={() => navigate('/nfp')}>
                Home
              </button>
              <button type="button" style={styles.logoutBtn} onClick={handleLogout}>
                Logout
              </button>
            </div>
          </div>

          {error ? <div style={styles.alertError}>{error}</div> : null}
          {notice ? <div style={styles.alertSuccess}>{notice}</div> : null}

          {loading ? (
            <div style={styles.loadingBlock}>
              <div style={styles.spinner} />
              <p>Loading verification…</p>
            </div>
          ) : (
            <div style={styles.stack}>
              <section style={styles.card}>
                <div style={styles.cardTitle}>Verification Summary</div>
                <div style={styles.summaryGrid}>
                  <SummaryRow label="Organization Type" value={verification?.summary?.organizationType} />
                  <SummaryRow label="Tax Classification" value={verification?.summary?.taxClassification} />
                  <SummaryRow label="Status Recognition" value={verification?.summary?.statusRecognition} />
                  <SummaryRow label="Parent Organization Name" value={verification?.summary?.parentOrganizationName} />
                  <SummaryRow label="GEN" value={verification?.summary?.groupExemptionNumber} />
                  <SummaryRow label="Recognized by Parent" value={verification?.summary?.recognizedByParent} />
                  <SummaryRow label="Covered by Parent Group Exemption" value={verification?.summary?.coveredByParentGroupExemption} />
                  <SummaryRow label="Government Entity Name" value={verification?.summary?.governmentEntityName} />
                  <SummaryRow label="Department / Division" value={verification?.summary?.governmentDivision} />
                  <SummaryRow label="Additional Notes" value={verification?.summary?.notes} />
                </div>
              </section>

              <section style={styles.card}>
                <div style={styles.cardTitle}>Required Documents</div>
                <div style={styles.sectionHelper}>{verification?.guidanceText}</div>
                <div style={styles.sectionMetaRow}>
                  <div style={styles.sectionMetaCard}>
                    <div style={styles.sectionMetaLabel}>Submitted documents</div>
                    <div style={styles.sectionMetaValue}>
                      {uploadedDocumentCount} of {requiredDocuments.length}
                    </div>
                  </div>
                  <div style={styles.sectionMetaCard}>
                    <div style={styles.sectionMetaLabel}>Current verification status</div>
                    <div style={styles.sectionMetaValue}>
                      {verification?.displayStatus?.label || 'Not started'}
                    </div>
                  </div>
                </div>
                <div style={styles.uploadList}>
                  {requiredDocuments.map((doc) => (
                    <VerificationUploadRow
                      key={doc.key}
                      doc={doc}
                      proofStatus={verification?.proofStatus}
                      canRemoveDraftUploads={!!verification?.canRemoveDraftUploads}
                      busy={busyKey === doc.key}
                      onChoose={(file) => handleUpload(doc.key, file)}
                      onRemove={() => handleRemove(doc.key)}
                    />
                  ))}
                </div>
                <div style={styles.submitRow}>
                  <button
                    type="button"
                    style={{
                      ...styles.primaryBtn,
                      ...(submitDisabled ? styles.primaryBtnDisabled : null),
                    }}
                    disabled={submitDisabled}
                    onClick={handleSubmit}
                  >
                    {submitLabel}
                  </button>
                  {!verification?.canSubmit ? (
                    <div style={styles.submitHint}>
                      {verification?.proofStatus === 'verified'
                        ? 'Verification is already complete.'
                        : verification?.proofStatus === 'pending_review'
                        ? 'Your documents are already in review.'
                        : 'Upload the required documents before submitting.'}
                    </div>
                  ) : null}
                </div>
              </section>

              <section style={styles.card}>
                <div style={styles.cardTitle}>Review Status</div>
                <div style={styles.reviewMessage}>{reviewMessage}</div>
                <div style={styles.summaryGrid}>
                  <SummaryRow label="Submitted" value={verification?.reviewStatus?.submittedAt ? new Date(verification.reviewStatus.submittedAt).toLocaleString() : ''} />
                  <SummaryRow label="Reviewed" value={verification?.reviewStatus?.reviewedAt ? new Date(verification.reviewStatus.reviewedAt).toLocaleString() : ''} />
                  <SummaryRow label="Verified" value={verification?.reviewStatus?.verifiedAt ? new Date(verification.reviewStatus.verifiedAt).toLocaleString() : ''} />
                  <SummaryRow label="Review Note" value={verification?.reviewStatus?.reasonNote || verification?.reviewStatus?.rejectionReason} />
                </div>
              </section>
            </div>
          )}
        </div>
      </div>
      <img src={rypilLo} alt="rypil mark" style={styles.badge} />
    </div>
  );
}

const styles = {
  fullscreen: {
    minHeight: '100vh',
    width: '100%',
    backgroundImage: `url(${tealBack})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    color: 'white',
  },
  dashboardMain: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, rgba(7,28,32,0.18), rgba(5,18,23,0.52))',
    padding: '32px 24px 48px',
    boxSizing: 'border-box',
  },
  dashboardInner: { maxWidth: 1240, margin: '0 auto' },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  headerTitle: { margin: 0, fontSize: 42, lineHeight: 1.05, fontWeight: 700 },
  headerSub: { marginTop: 10, fontSize: 16, opacity: 0.82, maxWidth: 760 },
  headerRight: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  stack: { display: 'grid', gap: 20 },
  card: {
    borderRadius: 20,
    padding: 22,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    backdropFilter: 'blur(14px)',
    boxShadow: '0 18px 60px rgba(6, 22, 28, 0.22)',
  },
  cardTitle: { fontSize: 24, fontWeight: 700, marginBottom: 14 },
  sectionHelper: { fontSize: 14, lineHeight: 1.55, opacity: 0.84, marginBottom: 16 },
  sectionMetaRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  sectionMetaCard: {
    borderRadius: 14,
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  sectionMetaLabel: {
    fontSize: 12,
    opacity: 0.68,
    marginBottom: 4,
  },
  sectionMetaValue: {
    fontSize: 18,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.96)',
  },
  summaryGrid: {
    display: 'grid',
    gap: 10,
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: '220px 1fr',
    gap: 12,
    padding: '6px 0',
  },
  summaryLabel: { fontSize: 13, opacity: 0.68 },
  summaryValue: { fontSize: 15, color: 'rgba(255,255,255,0.96)' },
  uploadList: { display: 'grid', gap: 14 },
  uploadRow: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.10)',
  },
  uploadHeader: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginBottom: 6,
  },
  uploadLabel: { fontSize: 16, fontWeight: 700 },
  docStateChip: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid',
    borderRadius: 999,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  uploadHelper: { fontSize: 14, lineHeight: 1.5, opacity: 0.9 },
  statusLine: {
    marginTop: 10,
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 13,
  },
  statusLineLabel: {
    opacity: 0.68,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontSize: 11,
    fontWeight: 700,
  },
  uploadState: {
    marginTop: 8,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
  },
  fileLink: { color: PEACH, textDecoration: 'none', fontWeight: 600 },
  uploadFallback: { marginTop: 8, fontSize: 12, lineHeight: 1.45, opacity: 0.68 },
  uploadActions: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  primaryBtn: {
    background: PEACH,
    color: '#1C1C1C',
    border: 'none',
    borderRadius: 14,
    padding: '12px 18px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
  },
  primaryBtnDisabled: {
    background: 'rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.56)',
    cursor: 'not-allowed',
  },
  secondaryBtn: {
    background: 'rgba(255,255,255,0.08)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 14,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  removeBtn: {
    background: 'transparent',
    color: '#FCA5A5',
    border: '1px solid rgba(248,113,113,0.38)',
    borderRadius: 14,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitRow: {
    marginTop: 18,
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  submitHint: { fontSize: 13, opacity: 0.72 },
  reviewMessage: {
    fontSize: 15,
    lineHeight: 1.55,
    marginBottom: 14,
    color: 'rgba(255,255,255,0.94)',
  },
  alertError: {
    border: '1px solid rgba(248,113,113,0.42)',
    background: 'rgba(127, 29, 29, 0.22)',
    borderRadius: 14,
    padding: '12px 14px',
    color: '#FCA5A5',
    marginBottom: 18,
  },
  alertSuccess: {
    border: '1px solid rgba(16,185,129,0.42)',
    background: 'rgba(6, 95, 70, 0.24)',
    borderRadius: 14,
    padding: '12px 14px',
    color: '#6EE7B7',
    marginBottom: 18,
  },
  loadingBlock: {
    minHeight: 260,
    display: 'grid',
    placeItems: 'center',
    gap: 12,
  },
  spinner: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: '3px solid rgba(255,255,255,0.2)',
    borderTopColor: PEACH,
    animation: 'spin 0.9s linear infinite',
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    border: '1px solid',
    borderRadius: 999,
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.04)',
    fontSize: 13,
    fontWeight: 600,
  },
  statusDot: { width: 8, height: 8, borderRadius: 999 },
  logoutBtn: {
    background: 'transparent',
    color: 'rgba(255,255,255,0.82)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 14,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  badge: {
    position: 'fixed',
    left: 24,
    bottom: 24,
    width: 74,
    height: 'auto',
    opacity: 0.95,
  },
};
